import { GenericObject } from '~/prix/types/type'

export interface MarkerCoordinates {
  lat: number
  lng: number
}

interface CoordinateKeyCandidate {
  key: string
  normalizedKey: string
  stem: string
  onStreet: boolean
}

const normalizeKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase()

const parseCoordinateNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number.parseFloat(value)
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

const buildCoordinates = (longitude: unknown, latitude: unknown): MarkerCoordinates | null => {
  const lng = parseCoordinateNumber(longitude)
  const lat = parseCoordinateNumber(latitude)

  if (lat === null || lng === null) {
    return null
  }

  return { lat, lng }
}

const extractCoordinatesFromPoint = (value: unknown) => {
  if (Array.isArray(value) && value.length >= 2) {
    return buildCoordinates(value[0], value[1])
  }

  if (!value || typeof value !== 'object') {
    return null
  }

  const coordinates = (value as { coordinates?: unknown }).coordinates
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    return buildCoordinates(coordinates[0], coordinates[1])
  }

  return null
}

const isLatitudeKey = (normalizedKey: string) => /\b(latitude|lat)\b/.test(normalizedKey)
const isLongitudeKey = (normalizedKey: string) => /\b(longitude|lng)\b/.test(normalizedKey)

const buildStem = (normalizedKey: string) =>
  normalizedKey
    .replace(/\b(latitude|longitude|lat|lng)\b/g, ' ')
    .replace(/\b(on street|street)\b/g, ' street ')
    .replace(/\b(point|coordinates?)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const buildCoordinateKeyCandidate = (key: string): CoordinateKeyCandidate | null => {
  const normalizedKey = normalizeKey(key)
  if (!isLatitudeKey(normalizedKey) && !isLongitudeKey(normalizedKey)) {
    return null
  }

  return {
    key,
    normalizedKey,
    stem: buildStem(normalizedKey),
    onStreet: normalizedKey.includes('street'),
  }
}

export function resolveMarkerCoordinates(item?: GenericObject | null): MarkerCoordinates | null {
  if (!item) {
    return null
  }

  const entries = Object.entries(item)

  const pointCoordinates = entries
    .map(([key, value]) => ({
      coordinates: extractCoordinatesFromPoint(value),
      priority: normalizeKey(key).includes('point on street') ? 2 : normalizeKey(key).includes('point') ? 1 : 0,
    }))
    .filter(entry => entry.coordinates && entry.priority > 0)
    .sort((left, right) => right.priority - left.priority)
    .at(0)?.coordinates

  if (pointCoordinates) {
    return pointCoordinates
  }

  const candidates = entries
    .map(([key]) => buildCoordinateKeyCandidate(key))
    .filter((candidate): candidate is CoordinateKeyCandidate => candidate !== null)

  const latitudeCandidates = candidates.filter(candidate => isLatitudeKey(candidate.normalizedKey))
  const longitudeCandidates = candidates.filter(candidate => isLongitudeKey(candidate.normalizedKey))

  let bestCoordinates: { coordinates: MarkerCoordinates; score: number } | null = null

  for (const latitudeCandidate of latitudeCandidates) {
    for (const longitudeCandidate of longitudeCandidates) {
      const coordinates = buildCoordinates(
        item[longitudeCandidate.key],
        item[latitudeCandidate.key],
      )

      if (!coordinates) {
        continue
      }

      const score =
        (latitudeCandidate.stem === longitudeCandidate.stem ? 100 : 0) +
        (latitudeCandidate.onStreet && longitudeCandidate.onStreet ? 40 : 0)

      if (!bestCoordinates || score > bestCoordinates.score) {
        bestCoordinates = {
          coordinates,
          score,
        }
      }
    }
  }

  return bestCoordinates?.coordinates ?? null
}
